import { FC, KeyboardEvent, useState, useEffect, useCallback } from 'react';
import { clsx } from 'clsx';
import {
  Menu,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import { LocalOfferOutlined, Search, ExpandMore } from '@mui/icons-material';
import { useRecoilState } from 'recoil';
import { TaskLabel } from '@demind-inc/core';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';

import './TaskFilter.scss';
import { useDropdownState } from '../../../hooks';
import { TaskFilterLabelNames, TaskFilterOptions, taskFilterAtom } from '../../../data-access';

interface TaskFilterProps {
  allLabels: TaskLabel[];
  className?: string;
}

const TaskFilter: FC<TaskFilterProps> = ({ allLabels = [], className }) => {
  const { t } = useTranslation();
  const { anchorEl, handleOpenDropdown, handleCloseDropdown } = useDropdownState();
  const [taskFilter, setTaskFilter] = useRecoilState(taskFilterAtom);
  const [selectedLabels, setSelectedLabels] = useState<TaskLabel[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    if (taskFilter.labelNames === 'lifestack_all') {
      setSelectedLabels(allLabels);
    } else if (Array.isArray(taskFilter.labelNames)) {
      const labels = allLabels.filter((label) => taskFilter.labelNames?.includes(label.name));
      setSelectedLabels(labels);
    }
  }, [taskFilter.labelNames, allLabels]);

  const handleFilterChange = useCallback(
    debounce((newVal: Partial<TaskFilterOptions>) => {
      setTaskFilter((prev) => ({ ...prev, ...newVal }));
    }, 1000),
    []
  );

  const handleLabelToggle = (label: TaskLabel) => {
    const isSelected = selectedLabels.some((l) => l.name === label.name);
    let newSelectedLabels: TaskLabel[];

    if (isSelected) {
      newSelectedLabels = selectedLabels.filter((l) => l.name !== label.name);
    } else {
      newSelectedLabels = [...selectedLabels, label];
    }

    setSelectedLabels(newSelectedLabels);

    let newLabelNames: TaskFilterLabelNames = [];
    if (newSelectedLabels.length === allLabels.length) {
      newLabelNames = 'lifestack_all';
    } else if (newSelectedLabels.length > 0) {
      newLabelNames = newSelectedLabels.map((l) => l.name);
    }

    handleFilterChange({
      labelNames: newLabelNames,
    });
  };

  const handleSelectAllLabel = (isSelected: boolean) => {
    const newSelectedLabels = isSelected ? allLabels : [];
    setSelectedLabels(newSelectedLabels);
    handleFilterChange({
      labelNames: isSelected ? 'lifestack_all' : [],
    });
  };

  const hasSelectedLabels = selectedLabels.length > 0 && selectedLabels.length < allLabels.length;

  const filteredLabels = allLabels.filter((label) =>
    label.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpenDropdown(event as any);
    }
  };

  return (
    <>
      <Stack
        className={clsx(
          'task-filter',
          className,
          anchorEl && 'task-filter--open',
          hasSelectedLabels && 'task-filter--selected'
        )}
        onClick={handleOpenDropdown}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        role="button"
        aria-label={t('task.filters.labelFilter')}
        tabIndex={0}
        onKeyDown={handleTriggerKeyDown}
      >
        <LocalOfferOutlined className="task-filter__icon" />
        <ExpandMore className="task-filter__arrow" />
      </Stack>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => {
          handleCloseDropdown();
          setSearchQuery('');
        }}
        className="task-filter__dropdown"
      >
        <div className="task-filter__dropdown__header">
          <Typography variant="body2" className="task-filter__dropdown__header__text">
            {t('task.filters.filterByLabels')}
          </Typography>
        </div>
        <div className="task-filter__dropdown__search">
          <TextField
            size="small"
            placeholder={t('task.filters.filterLabels')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            className="task-filter__dropdown__search__input"
          />
        </div>
        {filteredLabels.length > 0 ? (
          <FormGroup className="task-filter__dropdown__content">
            <FormControlLabel
              className={clsx('task-filter__dropdown__item', {
                'task-filter__dropdown__item--selected': selectedLabels.length === allLabels.length,
              })}
              control={
                <Checkbox
                  size="small"
                  checked={selectedLabels.length === allLabels.length}
                  onChange={(e) => handleSelectAllLabel(e.target.checked)}
                />
              }
              label={
                <div className="task-filter__dropdown__item__content">
                  <Typography variant="body2" className="task-filter__dropdown__item__text">
                    {t('task.filters.selectAll')}
                  </Typography>
                </div>
              }
            />
            {filteredLabels.map((label) => {
              const isSelected = selectedLabels.some((l) => l.name === label.name);
              return (
                <FormControlLabel
                  key={label.name}
                  className={clsx('task-filter__dropdown__item', {
                    'task-filter__dropdown__item--selected': isSelected,
                  })}
                  control={
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => handleLabelToggle(label)}
                    />
                  }
                  label={
                    <div className="task-filter__dropdown__item__content">
                      <div
                        className="task-filter__dropdown__item__color"
                        style={{ backgroundColor: label.color }}
                      />
                      <Typography variant="body2" className="task-filter__dropdown__item__text">
                        {label.name}
                      </Typography>
                    </div>
                  }
                />
              );
            })}
          </FormGroup>
        ) : (
          <div className="task-filter__dropdown__no-results">
            <Typography variant="body2" className="task-filter__dropdown__no-results__text">
              {t('task.filters.noLabels')}
            </Typography>
            <Typography variant="caption" className="task-filter__dropdown__no-results__subtext">
              {t('task.filters.tryDifferentQuery')}
            </Typography>
          </div>
        )}
      </Menu>
    </>
  );
};

export default TaskFilter;
